import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userAPI, zbioAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ProfileAvatar from './ProfileAvatar';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  profilePicture?: string;
  walletBalance: number;
}

const Profile: React.FC = () => {
  const { user, login, refreshAvatar, avatarKey } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [userBalance, setUserBalance] = useState<number>(0);
  const [zbioAddress, setZbioAddress] = useState<string>('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [editFormData, setEditFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
  });

  useEffect(() => {
    const loadWallet = async () => {
      try {
        const balanceRes = await zbioAPI.getBalance();
        setUserBalance(balanceRes.data.balance);
      } catch (error) {
        console.error('Error loading ZBIO balance:', error);
      }
      try {
        const walletRes = await zbioAPI.getMyWallet();
        setZbioAddress(walletRes.data.address);
      } catch (error) {
        console.error('Error loading ZBIO wallet address:', error);
      }
    };

    if (user) {
      loadWallet();
    }
  }, [user]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleEditProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await userAPI.updateProfile(editFormData);
      
      // Update the auth context with new user data
      const updatedUser = { ...user, ...response.data };
      login(updatedUser, localStorage.getItem('token') || '');
      
      setMessage({ text: t('profile.updateSuccess'), type: 'success' });
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ text: 'Failed to update profile. Please try again.', type: 'error' });
    }
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ text: 'Please select an image file', type: 'error' });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ text: 'File size must be less than 5MB', type: 'error' });
      return;
    }

    setIsUploadingPicture(true);
    try {
      const response = await userAPI.uploadProfilePicture(file);
      
      // Update the auth context with new user data
      const updatedUser = { ...user, profilePicture: response.profilePicture } as any;
      login(updatedUser, localStorage.getItem('token') || '');
      
      // Force re-render of ProfileAvatar components
      refreshAvatar();
      
      setMessage({ text: 'Profile picture updated successfully!', type: 'success' });
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setMessage({ text: 'Failed to upload profile picture. Please try again.', type: 'error' });
    } finally {
      setIsUploadingPicture(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="dashboard-modern">
      {/* Profile Header */}
      <div className="dashboard-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ position: 'relative' }}>
            <ProfileAvatar 
              key={`${user.id}-${user.profilePicture}-${avatarKey}`}
              user={user} 
              size={80} 
              onClick={handleProfilePictureClick}
              className="profile-picture-upload"
              cacheBuster={avatarKey}
            />
            {isUploadingPicture && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px'
              }}>
                {t('profile.uploading')}
              </div>
            )}
            <div style={{
              position: 'absolute',
              bottom: -5,
              right: -5,
              backgroundColor: '#4CAF50',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '12px',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
            }} onClick={handleProfilePictureClick}>
              📷
            </div>
          </div>
          <div>
            <h1>{user.firstName} {user.lastName}</h1>
            <p style={{ margin: 0, opacity: 0.8 }}>{user.email}</p>
            <p style={{ margin: '5px 0 0 0', color: '#4CAF50', fontWeight: 600 }}>
              💰 {userBalance.toLocaleString()} ZBIO
            </p>
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: 'none' }}
      />

      {/* Status message */}
      {message && (
        <div style={{
          padding: '12px 20px',
          margin: '20px 0',
          borderRadius: '8px',
          backgroundColor: message.type === 'success' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
          color: message.type === 'success' ? '#4CAF50' : '#f44336',
          border: `1px solid ${message.type === 'success' ? '#4CAF50' : '#f44336'}`,
        }}>
          {message.text}
        </div>
      )}

      {/* Profile Actions */}
      <div className="game-actions">
        <button 
          className="action-btn"
          onClick={() => setIsEditModalOpen(true)}
          style={{ background: '#2196F3' }}
        >
          <span>✏️</span>
          {t('profile.editProfile')}
        </button>
        <button 
          className="action-btn"
          onClick={() => navigate('/wallet')}
          style={{ background: '#FF9800' }}
        >
          <span>💰</span>
          {t('profile.manageZbio')}
        </button>
      </div>

      {/* Profile Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#FF9800' }}>💰</div>
          <div className="stat-label">{t('profile.zbioWallet')}</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#4CAF50', marginTop: '8px' }}>
            {userBalance.toLocaleString()}
          </div>
          <div style={{ fontSize: '14px', color: '#82878c' }}>{t('profile.zbio')}</div>
          {zbioAddress && (
            <div
              title={zbioAddress}
              onClick={() => {
                navigator.clipboard?.writeText(zbioAddress);
                setMessage({ text: t('profile.addressCopied'), type: 'success' });
              }}
              style={{
                marginTop: '10px',
                fontSize: '11px',
                color: '#82878c',
                fontFamily: 'monospace',
                cursor: 'pointer',
                wordBreak: 'break-all',
              }}
            >
              {zbioAddress.slice(0, 10)}…{zbioAddress.slice(-6)} 📋
            </div>
          )}
        </div>
        
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#2196F3' }}>🎮</div>
          <div className="stat-label">{t('profile.totalGames')}</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#2196F3', marginTop: '8px' }}>
            0
          </div>
          <div style={{ fontSize: '14px', color: '#82878c' }}>{t('profile.played')}</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#4CAF50' }}>👤</div>
          <div className="stat-label">{t('profile.profileInformation')}</div>
          <div style={{ fontSize: '14px', color: '#82878c', marginTop: '8px', textAlign: 'left' }}>
            <div><strong>{t('profile.firstName')}:</strong> {user.firstName}</div>
            <div><strong>{t('profile.lastName')}:</strong> {user.lastName}</div>
            <div><strong>{t('profile.email')}:</strong> {user.email}</div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {isEditModalOpen && (
        <div className="raub-modal-overlay" onClick={() => setIsEditModalOpen(false)}>
          <div className="raub-modal-content modern" onClick={(e) => e.stopPropagation()}>
            <div className="raub-modal-header">
              <h3>{t('profile.editProfile')}</h3>
              <button className="raub-modal-close" onClick={() => setIsEditModalOpen(false)}>×</button>
            </div>
            <form onSubmit={handleEditProfile}>
              <div className="raub-modal-body">
                <div className="raub-form-group">
                  <label>{t('profile.firstName')}:</label>
                  <input
                    type="text"
                    value={editFormData.firstName}
                    onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
                    required
                    className="raub-form-control"
                  />
                </div>
                <div className="raub-form-group">
                  <label>{t('profile.lastName')}:</label>
                  <input
                    type="text"
                    value={editFormData.lastName}
                    onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
                    required
                    className="raub-form-control"
                  />
                </div>
              </div>
              <div className="raub-modal-footer">
                <button type="button" className="raub-btn raub-btn-secondary" onClick={() => setIsEditModalOpen(false)}>
                  {t('profile.cancel')}
                </button>
                <button type="submit" className="raub-btn raub-btn-primary">
                  {t('profile.saveChanges')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Profile; 